import { useMemo, useState } from 'react';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, ComposedChart } from 'recharts';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { usePortfolioPnL } from '../../hooks/useCalculations';
import { resolvePackageCosts } from '../../lib/calculations';
import { formatCurrencyCompact, formatPrice } from '../../lib/formatters';
import { GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, TOOLTIP_STYLE, CHART_COLORS } from '../../styles/chart-theme';
import { InfoButton } from '../common/Tooltip';
import { Slider } from '../common/Slider';
import { Heart } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';

export function LTVCalculator() {
  const isMobile = useIsMobile();
  const packages = useSimulatorStore(s => s.packages);
  const assumptions = useSimulatorStore(s => s.assumptions);
  const portfolio = usePortfolioPnL();

  const [monthlyRetention, setMonthlyRetention] = useState(0.92);
  const [monthlyRecurring, setMonthlyRecurring] = useState(49);
  const [upsellRate, setUpsellRate] = useState(0.10);
  const [upsellRevenue, setUpsellRevenue] = useState(500);
  const [horizonMonths] = useState(24);

  const ltvData = useMemo(() => {
    return packages.map((pkg, i) => {
      const contrib = portfolio.packages[i];
      if (!contrib) return null;
      const costs = resolvePackageCosts(pkg, assumptions);
      const s1AcqPerUnit = costs.s2CloseRate > 0 ? costs.s1CostPerUnit / costs.s2CloseRate : 0;
      const totalAcqCost = s1AcqPerUnit + costs.s2RepCost;
      const months: { month: number; retained: number; cumulativeRevenue: number; cumulativeCost: number; netLTV: number }[] = [];
      let retained = 1.0;
      const featureCOGS = Object.entries(assumptions.featureCosts || {}).reduce((s, [f, c]) => s + (pkg.enabledFeatures.includes(f) ? c : 0), 0);
      // Ongoing monthly cost per retained customer (platform/service cost ~5% of recurring revenue)
      const monthlyServiceCost = monthlyRecurring * 0.05;
      let cumRevenue = 0;
      let cumCost = 0;

      for (let m = 0; m <= horizonMonths; m++) {
        if (m === 0) {
          cumCost = s1AcqPerUnit + costs.s2RepCost + featureCOGS;
          cumRevenue = pkg.price * (1 - costs.totalDeductionRate);
        } else {
          retained *= monthlyRetention;
          cumRevenue += retained * monthlyRecurring * (1 - costs.totalDeductionRate);
          cumCost += retained * monthlyServiceCost;
          if (m === 3) cumRevenue += retained * upsellRate * upsellRevenue * (1 - costs.totalDeductionRate);
        }
        months.push({
          month: m,
          retained: +(retained * 100).toFixed(1),
          cumulativeRevenue: Math.round(cumRevenue),
          cumulativeCost: Math.round(cumCost),
          netLTV: Math.round(cumRevenue - cumCost),
        });
      }

      const paybackMonth = months.findIndex(d => d.netLTV >= 0);
      const ltv24 = months[months.length - 1]?.netLTV ?? 0;

      return {
        name: pkg.name,
        price: pkg.price,
        acqCost: Math.round(totalAcqCost),
        months,
        paybackMonth: paybackMonth >= 0 ? paybackMonth : -1,
        ltv24,
        ltvToCAC: totalAcqCost > 0 ? +(((cumRevenue - cumCost) / totalAcqCost)).toFixed(1) : 0,
      };
    }).filter(Boolean);
  }, [packages, assumptions, portfolio, monthlyRetention, monthlyRecurring, upsellRate, upsellRevenue, horizonMonths]);

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <Heart size={16} className="text-chart-pink" />
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Customer Lifetime Value</h3>
        <InfoButton helpKey="ltvCalculator" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">
        Projected LTV per customer over 24 months. Determines whether loss-leader S1 tiers are viable through retention and upsells.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div>
          <label className="text-xs text-muted block mb-1">Monthly Retention</label>
          <div className="flex items-center gap-2">
            <Slider value={monthlyRetention} min={0.7} max={0.99} step={0.01} onChange={setMonthlyRetention} />
            <span className="text-xs font-mono text-primary w-10 shrink-0">{(monthlyRetention * 100).toFixed(0)}%</span>
          </div>
        </div>
        <div>
          <label className="text-xs text-muted block mb-1">Monthly Recurring $</label>
          <div className="flex items-center gap-2">
            <Slider value={monthlyRecurring} min={0} max={200} step={5} onChange={setMonthlyRecurring} />
            <span className="text-xs font-mono text-primary w-10 shrink-0">${monthlyRecurring}</span>
          </div>
        </div>
        <div>
          <label className="text-xs text-muted block mb-1">Upsell Rate (M3)</label>
          <div className="flex items-center gap-2">
            <Slider value={upsellRate} min={0} max={0.3} step={0.01} onChange={setUpsellRate} />
            <span className="text-xs font-mono text-primary w-10 shrink-0">{(upsellRate * 100).toFixed(0)}%</span>
          </div>
        </div>
        <div>
          <label className="text-xs text-muted block mb-1">Upsell Revenue</label>
          <div className="flex items-center gap-2">
            <Slider value={upsellRevenue} min={0} max={2000} step={50} onChange={setUpsellRevenue} />
            <span className="text-xs font-mono text-primary w-10 shrink-0">${upsellRevenue}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {ltvData.map(pkg => pkg && (
          <div key={pkg.name} className="px-3 py-2.5 rounded-lg" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <div className="text-xs font-semibold text-primary mb-2">{pkg.name} <span className="font-mono text-muted font-normal">({formatPrice(pkg.price)})</span></div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <div className="text-xs text-muted mb-0.5">Acq Cost</div>
                <div className="font-mono text-xs text-primary font-medium">{formatPrice(pkg.acqCost)}</div>
              </div>
              <div>
                <div className="text-xs text-muted mb-0.5">24mo LTV</div>
                <div className={`font-mono text-xs font-semibold ${pkg.ltv24 >= 0 ? 'text-metric-positive' : 'text-metric-negative'}`}>{formatCurrencyCompact(pkg.ltv24)}</div>
              </div>
              <div>
                <div className="text-xs text-muted mb-0.5">Payback</div>
                <div className="font-mono text-xs text-primary font-medium">{pkg.paybackMonth >= 0 ? `M${pkg.paybackMonth}` : 'N/A'}</div>
              </div>
              <div>
                <div className="text-xs text-muted mb-0.5">LTV:CAC</div>
                <div className={`font-mono text-xs font-semibold ${pkg.ltvToCAC >= 3 ? 'text-metric-positive' : pkg.ltvToCAC >= 1 ? 'text-chart-amber' : 'text-metric-negative'}`}>{pkg.ltvToCAC}x</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {ltvData.length > 0 && ltvData[0] && (
        <ResponsiveContainer width="100%" height={isMobile ? 180 : 240}>
          <ComposedChart data={ltvData[0].months} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="month" {...X_AXIS_PROPS} tickFormatter={v => `M${v}`} />
            <YAxis {...Y_AXIS_PROPS} tickFormatter={v => formatCurrencyCompact(v)} />
            <Tooltip {...TOOLTIP_STYLE} formatter={(v: number, name: string) => [formatCurrencyCompact(v), name === 'netLTV' ? 'Net LTV' : name === 'cumulativeRevenue' ? 'Cum. Revenue' : 'Cum. Cost']} labelFormatter={l => `Month ${l}`} />
            <ReferenceLine y={0} stroke="var(--color-reference-line)" strokeWidth={2} />
            <Area type="monotone" dataKey="netLTV" fill={CHART_COLORS.teal} fillOpacity={0.1} stroke={CHART_COLORS.teal} strokeWidth={2.5} />
            <Line type="monotone" dataKey="cumulativeRevenue" stroke={CHART_COLORS.emerald} strokeWidth={1.5} strokeDasharray="5 3" dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      )}

      <p className="text-xs text-muted mt-2 italic">
        LTV model assumes monthly recurring revenue from retained customers plus a one-time upsell opportunity at month 3. Retention compounds monthly. Chart shows {ltvData[0]?.name ?? 'first package'} only.
      </p>
    </div>
  );
}
